import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, ArrowRight, CheckCircle2, MapPin } from "lucide-react";

/**
 * Magic-flow recovery analytics card.
 *
 * Shows addresses that hit the "RentCast miss → manual form" fallback during
 * the cold-signup magic onboarding flow. Useful for spotting:
 *   • whether the manual form is a net save (resolution rate %)
 *   • geographic miss clusters that warrant a second data provider
 *   • churn risk (high abandoned count = users bouncing on signup)
 */
export default function MagicRecoveryCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/onboarding/magic-recovery/stats");
        if (!cancelled) setStats(data);
      } catch {
        // Non-admins / network errors silently hide the card.
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!stats) return null;
  // Hide entirely when there are no recoveries on record — nothing to learn from
  // a zero-count card, and it's cleaner on the admin dashboard.
  if (!stats.total) return null;

  const resPct = stats.resolution_rate_pct;
  const resTone = resPct >= 75 ? "emerald" : resPct >= 40 ? "amber" : "rose";

  return (
    <div className="card" data-testid="admin-magic-recovery-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs text-[var(--ink-3)] uppercase tracking-wider font-bold flex items-center gap-1.5">
            <AlertTriangle size={11} className="text-amber-500" /> Magic-flow recovery
          </div>
          <h3 className="font-display font-bold text-lg leading-tight">
            Addresses that needed a manual rescue
          </h3>
          <p className="text-xs text-[var(--ink-2)] mt-0.5">
            Cold-signup leads where RentCast had no data and we asked the user to fill in ARV/repairs themselves.
          </p>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <Kpi label="Total" value={stats.total} testid="recovery-total" />
        <Kpi label="Last 7 days" value={stats.last_7d} testid="recovery-7d" />
        <Kpi
          label="Resolved"
          value={stats.resolved}
          icon={CheckCircle2}
          tone="emerald"
          testid="recovery-resolved"
        />
        <Kpi
          label="Resolution rate"
          value={`${resPct}%`}
          tone={resTone}
          testid="recovery-rate"
        />
      </div>

      {stats.top_states?.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] text-[var(--ink-3)] uppercase tracking-wider font-bold mb-1.5">
            Top miss states
          </div>
          <div className="flex flex-wrap gap-1.5">
            {stats.top_states.map((s) => (
              <span
                key={s.state}
                className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                data-testid={`recovery-state-${s.state}`}
              >
                {s.state} · {s.count}
              </span>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-[11px] text-[var(--ink-3)] uppercase tracking-wider font-bold mb-1.5">
          Most recent misses
        </div>
        <ul className="text-sm divide-y divide-[var(--line)]" data-testid="recovery-recent-list">
          {stats.recent.slice(0, 6).map((r) => (
            <li
              key={r.id}
              className="py-2 flex items-center justify-between gap-2"
              data-testid={`recovery-row-${r.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate flex items-center gap-1.5">
                  <MapPin size={11} className="text-[var(--ink-3)] shrink-0" />
                  {r.property_address}
                </div>
                <div className="text-xs text-[var(--ink-3)] truncate">
                  {r.user_email || "—"}
                </div>
              </div>
              <div className="shrink-0">
                {r.magic_resolved_via_manual_form ? (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                    Resolved
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded">
                    Abandoned
                  </span>
                )}
              </div>
              <Link
                to={`/app/leads/${r.id}/report`}
                className="text-[var(--ink-3)] hover:text-[var(--primary)] shrink-0"
                aria-label="Open this lead"
              >
                <ArrowRight size={14} />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone = "ink", testid }) {
  const toneClasses = {
    ink: "text-[var(--ink)]",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };
  return (
    <div className="bg-[var(--bg-2)] rounded-lg px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--ink-3)] flex items-center gap-1">
        {Icon && <Icon size={10} />} {label}
      </div>
      <div className={`font-display font-extrabold text-xl ${toneClasses[tone]}`}>
        {value}
      </div>
    </div>
  );
}
